<template>
  <div>
    <component
      :is="tag"
      ref="scrollContainer"
      class="selection-list overflow-y-auto overscroll-none border-neutral-20 py-2"
      data-testid="selection-list"
      :class="{
        expanded: expanded,
        'scroll-top': scrollPosition === 'top',
        'scroll-bottom': scrollPosition === 'bottom',
        'has-overflow': hasOverflow,
      }"
      :style="{
        maxHeight: expanded ? maxHeight : '188px',
      }"
      aria-multiselectable="true"
      @keydown="handleKeydown"
    >
      <slot>
        <slot
          v-for="(item, index) in items"
          name="item"
          v-bind="{
            item,
            toggleItem,
            selectItem,
            deselectItem,
            isActive: isActive(item as any),
            tabindex: currentFocusIndex === index ? 0 : -1,
            onFocus: () => updateFocusIndex(index),
            onClick: () => {
              updateFocusIndex(index)
              toggleItem(item as object)
            },
          }"
        />
      </slot>
    </component>
    <FimButton
      v-if="hasOverflow"
      class="text-sm hover:!bg-transparent"
      data-testid="selection-list-toggle-button"
      no-padding
      is-small
      type="tertiary"
      @click="expanded = !expanded"
    >
      <span v-if="expanded">{{ $t('filter.show_less') }}</span>
      <span v-else>{{ $t('filter.show_all') }}</span>
    </FimButton>
  </div>
</template>

<script setup lang="ts">
import { throttle } from 'radash'

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  tag: {
    type: String as PropType<'div' | 'ul'>,
    enum: ['div', 'ul'],
    default: 'div',
  },
  value: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
})

const emit = defineEmits(['input'])

const scrollContainer = ref<HTMLElement>()

const selected = computed({
  get() {
    return props.value as object[]
  },
  set(newValue: object[]): void {
    emit('input', newValue)
  },
})

const toggleItem = (item: object) => {
  return !isActive(item) ? selectItem(item) : deselectItem(item)
}

const isActive = (item: object): boolean => {
  return selected.value.findIndex((i) => i === item) !== -1
}

const selectItem = (item: object) => {
  selected.value.push(item)
}

const deselectItem = (item: object) => {
  const index = selected.value.findIndex((i) => i === item)

  if (index !== -1) {
    selected.value.splice(index, 1)
  }
}

const maxHeight = computed(() => props.items.length * 29 + 'px')

const hasOverflow = computed(() => {
  return props.items.length > 6
})

const scrollTop = ref(0)
const onScroll = throttle({ interval: 25 }, () => {
  scrollTop.value = scrollContainer.value?.scrollTop || 0
})

onMounted(() => {
  nextTick(() => {
    scrollContainer.value?.addEventListener('scroll', onScroll, {
      passive: true,
    })
  })
})
onUnmounted(() => {
  scrollContainer.value?.removeEventListener('scroll', onScroll)
})

const scrollPosition = computed(() => {
  if (scrollTop.value === 0) {
    return 'top'
  }

  const maxScroll =
    (scrollContainer.value?.scrollHeight || 0) -
    (scrollContainer.value?.clientHeight || 0)

  if (scrollTop.value === maxScroll) {
    return 'bottom'
  }

  return 'middle'
})

const expanded = ref(false)
const currentFocusIndex = ref(0)

const handleKeydown = (event: KeyboardEvent) => {
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    navigateItems(-1)
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    navigateItems(1)
  }
}

// Navigate through the list using arrow keys (up/down) without scrolling
const navigateItems = (direction: number) => {
  const newIndex = currentFocusIndex.value + direction

  if (newIndex >= 0 && newIndex < props.items.length) {
    currentFocusIndex.value = newIndex

    nextTick(() => {
      const container = scrollContainer.value

      if (container) {
        const items = container.querySelectorAll('input[type="checkbox"]')
        const targetItem = items[newIndex] as HTMLElement

        if (targetItem) {
          targetItem.focus()
          targetItem.scrollIntoView({ block: 'nearest' })
        }
      }
    })
  }
}

// Update the current focus index when an item receives focus
const updateFocusIndex = (index: number) => {
  currentFocusIndex.value = index
}
</script>

<style lang="scss" scoped>
.selection-list {
  /* stylelint-disable-next-line plugin/no-low-performance-animation-properties */
  transition: max-height 0.4s;

  &.has-overflow {
    border-top: 1px;
    border-bottom: 1px;
    box-shadow:
      inset 0 8px 5px -8px rgb(0 0 0 / 0.2),
      inset 0 -8px 5px -8px rgb(0 0 0 / 0.2);

    &.scroll-top {
      border-top: 0;
      box-shadow: inset 0 -8px 5px -8px rgb(0 0 0 / 0.2);
    }

    &.scroll-bottom {
      border-bottom: 0;
      box-shadow: inset 0 8px 5px -8px rgb(0 0 0 / 0.2);
    }

    &.expanded {
      box-shadow: none;
      border: none;
    }
  }
}
</style>
